import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
    ArrowLeft, Calendar, ChevronLeft, ChevronRight, Plus, X,
    Clock, User, AlertTriangle, CheckCircle, Settings, Zap,
    FileText, Play, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

export default function ConsultantCalendarPage() {
    const navigate = useNavigate();
    const { authToken, user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [schedule, setSchedule] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [queue, setQueue] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showPreClinicNote, setShowPreClinicNote] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [preClinicNote, setPreClinicNote] = useState(null);
    const [autoScheduling, setAutoScheduling] = useState(false);
    const [loading, setLoading] = useState(true);
    const [subspecialties, setSubspecialties] = useState([]);

    // Schedule form
    const [scheduleForm, setScheduleForm] = useState({
        clinic_frequency: 'weekly',
        new_patients_per_clinic: 6,
        clinic_days: ['monday', 'wednesday'],
        clinic_start_time: '09:00',
        clinic_end_time: '17:00',
        max_urgent_per_clinic: 2,
        max_semi_urgent_per_clinic: 3,
        subspecialties: [],
        extra_clinic_dates: []
    });
    const [newExtraDate, setNewExtraDate] = useState('');

    const headers = { Authorization: `Bearer ${authToken}` };

    useEffect(() => {
        if (authToken) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, currentDate]);

    const fetchData = async () => {
        try {
            const [scheduleRes, queueRes, subspecRes] = await Promise.all([
                axios.get(`${API}/consultant/schedule`, { headers }),
                axios.get(`${API}/appointment-queue`, { headers }),
                axios.get(`${API}/consultant/subspecialties`, { headers })
            ]);
            
            setSchedule(scheduleRes.data);
            setQueue(queueRes.data.filter(q => q.status === 'queued'));
            setSubspecialties(subspecRes.data.subspecialties || []);
            
            // Populate form with existing schedule
            if (scheduleRes.data) {
                setScheduleForm({
                    clinic_frequency: scheduleRes.data.clinic_frequency || 'weekly',
                    new_patients_per_clinic: scheduleRes.data.new_patients_per_clinic || 6,
                    clinic_days: scheduleRes.data.clinic_days || ['monday', 'wednesday'],
                    clinic_start_time: scheduleRes.data.clinic_start_time || '09:00',
                    clinic_end_time: scheduleRes.data.clinic_end_time || '17:00',
                    max_urgent_per_clinic: scheduleRes.data.max_urgent_per_clinic || 2,
                    max_semi_urgent_per_clinic: scheduleRes.data.max_semi_urgent_per_clinic || 3,
                    subspecialties: scheduleRes.data.subspecialties || [],
                    extra_clinic_dates: scheduleRes.data.extra_clinic_dates || []
                });
            }
            
            // Convert scheduled items to appointments
            const scheduled = queueRes.data.filter(q => q.status === 'scheduled');
            setAppointments(scheduled);
            
            if (scheduleRes.data && Object.keys(scheduleRes.data).length > 0) {
                setScheduleForm({
                    ...scheduleForm,
                    ...scheduleRes.data
                });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveSchedule = async () => {
        try {
            await axios.post(`${API}/consultant/schedule`, scheduleForm, { headers });
            toast.success('Schedule saved successfully');
            setShowScheduleModal(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to save schedule');
        }
    };

    const blockDate = async (date) => {
        try {
            await axios.put(`${API}/consultant/schedule/block-dates`, {
                dates: [date]
            }, { headers });
            toast.success('Date blocked');
            fetchData();
        } catch (error) {
            toast.error('Failed to block date');
        }
    };

    const unblockDate = async (date) => {
        try {
            await axios.put(`${API}/consultant/schedule/unblock-dates`, {
                dates: [date]
            }, { headers });
            toast.success('Date unblocked');
            fetchData();
        } catch (error) {
            toast.error('Failed to unblock date');
        }
    };

    const runAutoScheduling = async () => {
        setAutoScheduling(true);
        try {
            const response = await axios.post(`${API}/scheduling/run-auto-scheduling`, {}, { headers });
            toast.success(`Auto-scheduled ${response.data.scheduled} appointments`);
            fetchData();
        } catch (error) {
            toast.error('Auto-scheduling failed');
        } finally {
            setAutoScheduling(false);
        }
    };

    const autoScheduleSingle = async (queueId, referralId) => {
        try {
            const response = await axios.post(`${API}/scheduling/auto-schedule/${referralId}`, {}, { headers });
            toast.success(`Scheduled for ${response.data.scheduled_date} at ${response.data.scheduled_time}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to auto-schedule');
        }
    };

    const viewPreClinicNote = async (referralId) => {
        try {
            const response = await axios.get(`${API}/referrals/${referralId}/pre-clinic-note`, { headers });
            setPreClinicNote(response.data);
            setSelectedReferral(referralId);
            setShowPreClinicNote(true);
        } catch (error) {
            toast.error('Failed to load pre-clinic note');
        }
    };

    const scheduleAppointment = async (queueId, date, time) => {
        try {
            await axios.put(`${API}/appointment-queue/${queueId}/schedule?appointment_date=${date}&appointment_time=${time}`, {}, { headers });
            toast.success('Appointment scheduled');
            fetchData();
        } catch (error) {
            toast.error('Failed to schedule appointment');
        }
    };

    // Calendar helpers
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        
        // Add empty cells for days before first of month
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }
        
        // Add days of month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }
        
        return days;
    };

    const isClinicDay = (date) => {
        if (!date || !schedule?.clinic_days) return false;
        const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayMap[date.getDay()];
        return schedule.clinic_days.includes(dayName);
    };

    const isBlockedDate = (date) => {
        if (!date || !schedule?.blocked_dates) return false;
        const dateStr = date.toISOString().split('T')[0];
        return schedule.blocked_dates.includes(dateStr);
    };

    const getAppointmentsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return appointments.filter(a => a.scheduled_date === dateStr);
    };

    const handleDragStart = (item) => {
        setDraggedItem(item);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (date) => {
        if (!draggedItem || !date) return;
        
        const dateStr = date.toISOString().split('T')[0];
        if (isBlockedDate(date)) {
            toast.error('Cannot schedule on blocked date');
            return;
        }
        
        // Default time slot
        const time = '09:00';
        scheduleAppointment(draggedItem.id, dateStr, time);
        setDraggedItem(null);
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const getUrgencyColor = (level) => {
        switch (level) {
            case 'urgent': return 'bg-red-500';
            case 'semi_urgent': return 'bg-amber-500';
            case 'moderate': return 'bg-amber-500'; // backward compatibility
            default: return 'bg-emerald-500';
        }
    };

    const days = getDaysInMonth(currentDate);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Calendar className="w-8 h-8 animate-pulse text-teal-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50" data-testid="consultant-calendar">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/consultant')}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Dashboard
                        </Button>
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-teal-600" />
                            <span className="font-semibold text-slate-900">Clinic Calendar</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setShowScheduleModal(true)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Schedule Settings
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Queue Panel */}
                    <div className="lg:col-span-1">
                        <Card className="sticky top-24">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="text-base">Waiting Queue</CardTitle>
                                        <CardDescription>Drag patients to calendar</CardDescription>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={runAutoScheduling}
                                        disabled={autoScheduling || queue.length === 0}
                                        data-testid="auto-schedule-all-btn"
                                        className="text-xs"
                                    >
                                        <Zap className="w-3 h-3 mr-1" />
                                        {autoScheduling ? 'Scheduling...' : 'Auto All'}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                                {queue.length === 0 ? (
                                    <p className="text-sm text-slate-500 text-center py-4">No patients in queue</p>
                                ) : (
                                    queue.map(item => (
                                        <div
                                            key={item.id}
                                            draggable
                                            onDragStart={() => handleDragStart(item)}
                                            className="p-3 bg-white border rounded-lg cursor-grab hover:shadow-md transition-shadow"
                                            data-testid={`queue-item-${item.id}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-sm truncate">{item.patient_name}</span>
                                                <Badge className={`${getUrgencyColor(item.urgency_level)} text-white text-xs`}>
                                                    {item.urgency_level}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-slate-500">{item.condition?.replace(/_/g, ' ')}</p>
                                            <p className="text-xs text-slate-400 mt-1">Target: {item.target_date}</p>
                                            <div className="flex gap-1 mt-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        autoScheduleSingle(item.id, item.referral_id);
                                                    }}
                                                    data-testid={`auto-schedule-${item.id}`}
                                                >
                                                    <Zap className="w-3 h-3 mr-1" />
                                                    Auto
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        viewPreClinicNote(item.referral_id);
                                                    }}
                                                    data-testid={`view-note-${item.id}`}
                                                >
                                                    <FileText className="w-3 h-3 mr-1" />
                                                    Note
                                                </Button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Calendar */}
                    <div className="lg:col-span-3">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <Button variant="ghost" size="sm" onClick={prevMonth}>
                                            <ChevronLeft className="w-5 h-5" />
                                        </Button>
                                        <h2 className="text-lg font-semibold">
                                            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                                        </h2>
                                        <Button variant="ghost" size="sm" onClick={nextMonth}>
                                            <ChevronRight className="w-5 h-5" />
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs">
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 bg-teal-100 rounded"></span> Clinic Day
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 bg-red-100 rounded"></span> Blocked
                                        </span>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* Calendar Grid */}
                                <div className="grid grid-cols-7 gap-1">
                                    {/* Day Headers */}
                                    {DAYS.map(day => (
                                        <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                                            {day}
                                        </div>
                                    ))}
                                    
                                    {/* Calendar Days */}
                                    {days.map((date, index) => {
                                        if (!date) {
                                            return <div key={`empty-${index}`} className="h-28"></div>;
                                        }
                                        
                                        const isToday = date.toDateString() === new Date().toDateString();
                                        const isClinic = isClinicDay(date);
                                        const isBlocked = isBlockedDate(date);
                                        const dayAppointments = getAppointmentsForDate(date);
                                        const dateStr = date.toISOString().split('T')[0];
                                        
                                        return (
                                            <div
                                                key={dateStr}
                                                onDragOver={handleDragOver}
                                                onDrop={() => handleDrop(date)}
                                                className={`h-28 border rounded-lg p-1 transition-colors ${
                                                    isBlocked ? 'bg-red-50 border-red-200' :
                                                    isClinic ? 'bg-teal-50 border-teal-200' :
                                                    'bg-white border-slate-200'
                                                } ${isToday ? 'ring-2 ring-teal-500' : ''}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className={`text-sm font-medium ${
                                                        isToday ? 'text-teal-600' : 'text-slate-700'
                                                    }`}>
                                                        {date.getDate()}
                                                    </span>
                                                    {isClinic && !isBlocked && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-5 w-5 p-0"
                                                            onClick={() => blockDate(dateStr)}
                                                            title="Block date"
                                                        >
                                                            <X className="w-3 h-3 text-slate-400" />
                                                        </Button>
                                                    )}
                                                    {isBlocked && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-5 w-5 p-0"
                                                            onClick={() => unblockDate(dateStr)}
                                                            title="Unblock date"
                                                        >
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                                
                                                {/* Appointments */}
                                                <div className="mt-1 space-y-1 overflow-y-auto max-h-16">
                                                    {dayAppointments.map(apt => (
                                                        <div
                                                            key={apt.id}
                                                            className={`text-xs p-1 rounded ${getUrgencyColor(apt.urgency_level)} text-white truncate`}
                                                            title={apt.patient_name}
                                                        >
                                                            {apt.scheduled_time} {apt.patient_name?.split(' ')[0]}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Schedule Settings Modal */}
            {showScheduleModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-lg mx-4 my-8">
                        <CardHeader>
                            <CardTitle>Schedule Settings</CardTitle>
                            <CardDescription>Configure your clinic availability and subspecialties</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
                            {/* Subspecialties Section */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">My Subspecialties</Label>
                                <p className="text-xs text-slate-500">Select the areas you specialize in. Referrals will be matched accordingly.</p>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    {subspecialties.map(spec => (
                                        <label key={spec.id} className="flex items-center gap-2 text-sm p-2 border rounded-md hover:bg-slate-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={scheduleForm.subspecialties.includes(spec.id)}
                                                onChange={(e) => {
                                                    const subs = e.target.checked
                                                        ? [...scheduleForm.subspecialties, spec.id]
                                                        : scheduleForm.subspecialties.filter(s => s !== spec.id);
                                                    setScheduleForm({...scheduleForm, subspecialties: subs});
                                                }}
                                                className="rounded"
                                                data-testid={`subspecialty-${spec.id}`}
                                            />
                                            {spec.name}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <Label>Clinic Frequency</Label>
                                <select
                                    className="w-full p-2 border rounded-md"
                                    value={scheduleForm.clinic_frequency}
                                    onChange={(e) => setScheduleForm({...scheduleForm, clinic_frequency: e.target.value})}
                                    data-testid="clinic-frequency-select"
                                >
                                    <option value="weekly">Weekly</option>
                                    <option value="fortnightly">Fortnightly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <Label>Regular Clinic Days</Label>
                                <div className="flex flex-wrap gap-2">
                                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => (
                                        <label key={day} className="flex items-center gap-1 text-sm p-2 border rounded-md hover:bg-slate-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={scheduleForm.clinic_days.includes(day)}
                                                onChange={(e) => {
                                                    const days = e.target.checked
                                                        ? [...scheduleForm.clinic_days, day]
                                                        : scheduleForm.clinic_days.filter(d => d !== day);
                                                    setScheduleForm({...scheduleForm, clinic_days: days});
                                                }}
                                                data-testid={`clinic-day-${day}`}
                                            />
                                            {day.charAt(0).toUpperCase() + day.slice(1)}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Extra Clinic Dates Section */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Extra Clinic Dates</Label>
                                <p className="text-xs text-slate-500">Add one-off extra clinic days outside your regular schedule</p>
                                <div className="flex gap-2">
                                    <Input
                                        type="date"
                                        value={newExtraDate}
                                        onChange={(e) => setNewExtraDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="flex-1"
                                        data-testid="extra-date-input"
                                    />
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => {
                                            if (newExtraDate && !scheduleForm.extra_clinic_dates.includes(newExtraDate)) {
                                                setScheduleForm({
                                                    ...scheduleForm, 
                                                    extra_clinic_dates: [...scheduleForm.extra_clinic_dates, newExtraDate].sort()
                                                });
                                                setNewExtraDate('');
                                                toast.success('Extra clinic date added');
                                            }
                                        }}
                                        data-testid="add-extra-date-btn"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                {scheduleForm.extra_clinic_dates.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {scheduleForm.extra_clinic_dates.map(date => (
                                            <Badge key={date} variant="secondary" className="flex items-center gap-1">
                                                {date}
                                                <button
                                                    onClick={() => {
                                                        setScheduleForm({
                                                            ...scheduleForm,
                                                            extra_clinic_dates: scheduleForm.extra_clinic_dates.filter(d => d !== date)
                                                        });
                                                    }}
                                                    className="ml-1 hover:text-red-500"
                                                    data-testid={`remove-date-${date}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Separator />

                            {/* Clinic Session Preset */}
                            <div className="space-y-3">
                                <Label className="font-medium">Clinic Session Time</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setScheduleForm({
                                            ...scheduleForm,
                                            clinic_start_time: '09:00',
                                            clinic_end_time: '12:30',
                                            clinic_session: 'morning'
                                        })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            scheduleForm.clinic_session === 'morning' || 
                                            (scheduleForm.clinic_start_time === '09:00' && scheduleForm.clinic_end_time === '12:30')
                                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                                : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                        data-testid="session-morning"
                                    >
                                        <div className="font-semibold">Morning</div>
                                        <div className="text-xs text-slate-500">09:00 - 12:30</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setScheduleForm({
                                            ...scheduleForm,
                                            clinic_start_time: '13:30',
                                            clinic_end_time: '17:00',
                                            clinic_session: 'afternoon'
                                        })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            scheduleForm.clinic_session === 'afternoon' ||
                                            (scheduleForm.clinic_start_time === '13:30' && scheduleForm.clinic_end_time === '17:00')
                                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                                : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                        data-testid="session-afternoon"
                                    >
                                        <div className="font-semibold">Afternoon</div>
                                        <div className="text-xs text-slate-500">13:30 - 17:00</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setScheduleForm({
                                            ...scheduleForm,
                                            clinic_start_time: '17:00',
                                            clinic_end_time: '20:30',
                                            clinic_session: 'evening'
                                        })}
                                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                                            scheduleForm.clinic_session === 'evening' ||
                                            (scheduleForm.clinic_start_time === '17:00' && scheduleForm.clinic_end_time === '20:30')
                                                ? 'border-teal-500 bg-teal-50 text-teal-700'
                                                : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                        data-testid="session-evening"
                                    >
                                        <div className="font-semibold">Evening</div>
                                        <div className="text-xs text-slate-500">17:00 - 20:30</div>
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500">Or set custom times below:</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Time</Label>
                                    <Input
                                        type="time"
                                        value={scheduleForm.clinic_start_time}
                                        onChange={(e) => setScheduleForm({...scheduleForm, clinic_start_time: e.target.value, clinic_session: 'custom'})}
                                        data-testid="clinic-start-time"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>End Time</Label>
                                    <Input
                                        type="time"
                                        value={scheduleForm.clinic_end_time}
                                        onChange={(e) => setScheduleForm({...scheduleForm, clinic_end_time: e.target.value, clinic_session: 'custom'})}
                                        data-testid="clinic-end-time"
                                    />
                                </div>
                            </div>

                            {/* Consultation Duration */}
                            <div className="space-y-2">
                                <Label className="font-medium">Consultation Duration</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        min="15"
                                        max="60"
                                        step="5"
                                        value={scheduleForm.consultation_duration || 30}
                                        onChange={(e) => setScheduleForm({...scheduleForm, consultation_duration: parseInt(e.target.value)})}
                                        className="w-24"
                                        data-testid="consultation-duration"
                                    />
                                    <span className="text-sm text-slate-500">minutes per patient</span>
                                </div>
                                <p className="text-xs text-slate-500">
                                    Based on {scheduleForm.consultation_duration || 30} min slots, you can see approximately {
                                        Math.floor((
                                            (parseInt(scheduleForm.clinic_end_time?.split(':')[0] || 17) * 60 + parseInt(scheduleForm.clinic_end_time?.split(':')[1] || 0)) -
                                            (parseInt(scheduleForm.clinic_start_time?.split(':')[0] || 9) * 60 + parseInt(scheduleForm.clinic_start_time?.split(':')[1] || 0))
                                        ) / (scheduleForm.consultation_duration || 30))
                                    } patients per clinic session.
                                </p>
                            </div>

                            <Separator />

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>New Patients Per Clinic</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={scheduleForm.new_patients_per_clinic}
                                        onChange={(e) => setScheduleForm({...scheduleForm, new_patients_per_clinic: parseInt(e.target.value)})}
                                        data-testid="patients-per-clinic"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max Urgent/Clinic</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={scheduleForm.max_urgent_per_clinic}
                                        onChange={(e) => setScheduleForm({...scheduleForm, max_urgent_per_clinic: parseInt(e.target.value)})}
                                        data-testid="max-urgent-per-clinic"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 pt-4">
                                <Button variant="outline" onClick={() => setShowScheduleModal(false)} className="flex-1" data-testid="cancel-schedule-btn">
                                    Cancel
                                </Button>
                                <Button onClick={saveSchedule} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="save-schedule-btn">
                                    Save Settings
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Pre-Clinic Note Modal */}
            {showPreClinicNote && preClinicNote && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <CardHeader className="flex-shrink-0 border-b">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-teal-600" />
                                        Pre-Clinic Note
                                    </CardTitle>
                                    <CardDescription>{preClinicNote.patient_name}</CardDescription>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => setShowPreClinicNote(false)}>
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-6">
                            {/* Patient Identification */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Patient Identification</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div><span className="font-medium">Name:</span> {preClinicNote.patient_name}</div>
                                    <div><span className="font-medium">NHS Number:</span> {preClinicNote.nhs_number || 'Not provided'}</div>
                                    <div><span className="font-medium">Date of Birth:</span> {preClinicNote.date_of_birth}</div>
                                    <div><span className="font-medium">Age:</span> {preClinicNote.age} years</div>
                                    <div><span className="font-medium">Gender:</span> {preClinicNote.gender}</div>
                                </div>
                            </div>

                            {/* Diagnosis */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Diagnosis / Reason for Referral</h3>
                                <div className="text-sm space-y-2">
                                    <div><span className="font-medium">Diagnosis:</span> {preClinicNote.diagnosis}</div>
                                    <div><span className="font-medium">Duration of Symptoms:</span> {preClinicNote.duration_of_symptoms}</div>
                                    <div><span className="font-medium">Urgency Level:</span> 
                                        <Badge className={`ml-2 ${getUrgencyColor(preClinicNote.urgency_level)} text-white`}>
                                            {preClinicNote.urgency_level?.replace(/_/g, ' ')}
                                        </Badge>
                                    </div>
                                </div>
                            </div>

                            {/* Associated Symptoms */}
                            {preClinicNote.associated_symptoms?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Associated Symptoms</h3>
                                    <ul className="text-sm list-disc list-inside">
                                        {preClinicNote.associated_symptoms.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                </div>
                            )}

                            {/* Clinical Scores */}
                            {preClinicNote.clinical_scores && Object.keys(preClinicNote.clinical_scores).length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Scores</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {Object.entries(preClinicNote.clinical_scores).map(([name, data]) => (
                                            <div key={name} className="bg-slate-50 p-3 rounded">
                                                <div className="font-medium">{name}</div>
                                                {typeof data === 'object' ? (
                                                    <>
                                                        <div>Score: {data.score}</div>
                                                        <div className="text-slate-500">({data.interpretation})</div>
                                                    </>
                                                ) : (
                                                    <div>{data}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* BMI & HbA1c */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Physical Measurements</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div><span className="font-medium">BMI:</span> {preClinicNote.bmi} ({preClinicNote.bmi_category?.replace(/_/g, ' ')})</div>
                                    {preClinicNote.hba1c && <div><span className="font-medium">HbA1c:</span> {preClinicNote.hba1c}</div>}
                                    {preClinicNote.diabetes_status && <div><span className="font-medium">Diabetes:</span> {preClinicNote.diabetes_status}</div>}
                                </div>
                            </div>

                            {/* Interventions to Date */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Interventions to Date</h3>
                                <ul className="text-sm list-disc list-inside">
                                    {preClinicNote.interventions_to_date?.map((i, idx) => <li key={idx}>{i}</li>)}
                                </ul>
                            </div>

                            {/* Past Medical History */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Past Medical History</h3>
                                <ul className="text-sm list-disc list-inside">
                                    {preClinicNote.past_medical_history?.map((h, i) => <li key={i}>{h}</li>)}
                                </ul>
                            </div>

                            {/* Past Surgical History */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Past Surgical History</h3>
                                <ul className="text-sm list-disc list-inside">
                                    {preClinicNote.past_surgical_history?.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            </div>

                            {/* Medications */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Current Medications</h3>
                                {preClinicNote.medications?.length > 0 ? (
                                    <ul className="text-sm list-disc list-inside">
                                        {preClinicNote.medications.map((m, i) => (
                                            <li key={i}>
                                                {typeof m === 'string' ? m : `${m.name} ${m.dose || ''} ${m.frequency || ''}`}
                                                {m.note && <span className="text-amber-600 font-medium"> ** {m.note} **</span>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-slate-500">No medications documented</p>
                                )}
                            </div>

                            {/* Allergies */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Allergies</h3>
                                <p className="text-sm">{preClinicNote.allergies || 'No known drug allergies (NKDA)'}</p>
                            </div>

                            {/* Examination Findings */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Clinical Examination Findings</h3>
                                <p className="text-sm">{preClinicNote.examination_findings || 'Not documented'}</p>
                            </div>

                            {/* Investigations */}
                            {preClinicNote.investigations?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Investigations</h3>
                                    <ul className="text-sm space-y-2">
                                        {preClinicNote.investigations.map((inv, i) => (
                                            <li key={i} className="bg-slate-50 p-2 rounded">
                                                <span className="font-medium">{inv.type}:</span> {inv.findings || inv.filename}
                                                {inv.description && <p className="text-slate-500 text-xs">{inv.description}</p>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Referring Doctor */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-slate-900 mb-3 border-b pb-2">Referral Information</h3>
                                <div className="text-sm">
                                    <div><span className="font-medium">Referring Doctor:</span> {preClinicNote.referring_doctor}</div>
                                    <div><span className="font-medium">Referral Date:</span> {preClinicNote.referral_date?.slice(0, 10)}</div>
                                </div>
                            </div>
                        </CardContent>
                        <div className="flex-shrink-0 border-t p-4 bg-slate-50">
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setShowPreClinicNote(false)}>
                                    Close
                                </Button>
                                <Button 
                                    className="bg-teal-600 hover:bg-teal-700"
                                    onClick={() => {
                                        if (preClinicNote.formatted_note) {
                                            navigator.clipboard.writeText(preClinicNote.formatted_note);
                                            toast.success('Note copied to clipboard');
                                        }
                                    }}
                                >
                                    Copy Note
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
